import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSetAtom } from "jotai";
import { Icon } from "assets/icons";
import { useItemGroups } from "atoms/itemsGropAtom";
import { ItemsGroup, ItemsGroupWithChildren } from "types";
import { transformItemsGroup } from "features/ProductsGroup/helpers";
import { useEditProduct } from "features/ProductProperties/api";
import { setModalCloseAtom } from "atoms/modalAtom/modalAtom";
import { useItems } from "atoms/itemAtom";
import {
  useEditProductsGroup,
  useGetProductsGroup,
} from "features/ProductsGroup/api";

import { Button, EButtonVariant } from "components/Button/Button";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";
import { Accordion } from "components/Accordion/Accordion";
import Radio from "components/Radio/Radio";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { BoldText } from "components/BoldText/BoldText";

import styles from "./styles.module.css";

type MoveModalItemProps = {
  title: string;
  isEditGroup?: boolean;
};

const MAX_GROUP_NAME_LENGTH = 36;

function truncateGroupName(name: string): string {
  if (!name || name.length <= MAX_GROUP_NAME_LENGTH) return name;
  const partLen = Math.floor((MAX_GROUP_NAME_LENGTH - 3) / 2);
  return `${name.slice(0, partLen)}…${name.slice(-partLen)}`;
}

const RadioContainer = ({ children }: PropsWithChildren): JSX.Element => (
  <div className="items-center w-full mb-2">{children}</div>
);

export const RadioButtons: FC<MoveModalItemProps> = ({
  title,
  isEditGroup,
}) => {
  const queryClient = useQueryClient();
  const [selectedRadio, setSelectedRadio] = useState<string | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set()
  );
  const closeModal = useSetAtom(setModalCloseAtom);

  const { itemGroupsState } = useItemGroups();
  const { itemsState } = useItems();
  const { activeItemsGroup } = itemGroupsState;
  const { checkedItems } = itemsState;

  const { data: productGroups = [] } = useGetProductsGroup({ enabled: false });

  const { mutate: editProductsGroup } = useEditProductsGroup();

  const { mutate: editProduct } = useEditProduct({});

  const filteredProductGroups = useMemo(() => {
    if (isEditGroup && activeItemsGroup) {
      return productGroups.filter(
        (item: ItemsGroup) =>
          item.parentid !== activeItemsGroup.itemgroupid &&
          item.itemgroupid !== activeItemsGroup.itemgroupid
      );
    }

    return productGroups;
  }, [productGroups, activeItemsGroup, checkedItems, isEditGroup]);

  const productGroupItems: ItemsGroupWithChildren[] = transformItemsGroup([
    ...filteredProductGroups,
  ]);

  const toggleAccordion = (itemgroupid: string) => {
    setExpandedAccordions((prevState) => {
      const newExpanded = new Set(prevState);
      if (newExpanded.has(itemgroupid)) {
        newExpanded.delete(itemgroupid);
      } else {
        newExpanded.add(itemgroupid);
      }
      return newExpanded;
    });
  };

  const handleRadioChange = (itemgroupid: string) => {
    setSelectedRadio(itemgroupid);
  };

  const handleSave = async () => {
    if (!selectedRadio) return;

    const groupId = activeItemsGroup?.itemgroupid;
    const currentGroupFromList = productGroups.find(
      (g: ItemsGroup) => g.itemgroupid === groupId
    );
    const sourceGroup = currentGroupFromList ?? activeItemsGroup;

    const editItemGroupParams = {
      groupid: groupId,
      name: sourceGroup?.name || "",
      parentid: selectedRadio,
      importcode: sourceGroup?.importcode ?? activeItemsGroup?.importcode,
      position:
        typeof sourceGroup?.position === "number"
          ? sourceGroup.position
          : typeof activeItemsGroup?.position === "number"
            ? activeItemsGroup.position
            : 0,
      avaliable: sourceGroup?.avaliable ?? activeItemsGroup?.avaliable ? 1 : 0,
      onlywhole: sourceGroup?.onlywhole ?? activeItemsGroup?.onlywhole ? 1 : 0,
    };

    const editProductParams = {
      itemid: checkedItems[0]?.itemid || "",
      name: checkedItems[0]?.name || "",
      itemgroupid: +selectedRadio,
      avaliable: checkedItems[0]?.avaliable,
      nodiscount: checkedItems[0]?.nodiscount,
      service: checkedItems[0]?.service,
      measureid: checkedItems[0]?.measureid,
      onlywhole: checkedItems[0]?.onlywhole,
      discription: checkedItems[0]?.discription,
      nobonus: checkedItems[0]?.nobonus,
      deleted: 0,
      position: checkedItems[0]?.position,
      upload: checkedItems[0]?.upload,
      ndsid: checkedItems[0]?.ndsid,
      importcode: checkedItems[0]?.importcode,
      freeprice: checkedItems[0]?.freeprice,
      code: checkedItems[0]?.code,
      siteimportcode: checkedItems[0]?.siteimportcode,
    };

    if (isEditGroup) {
      editProductsGroup(editItemGroupParams);

      queryClient.invalidateQueries({ queryKey: ["get-products"] });
    } else {
      editProduct(editProductParams);
    }

    closeModal();
  };

  const renderItems = (items: ItemsGroupWithChildren[]) =>
    items.map((item) => {
      const { children } = item;
      const isExpanded = expandedAccordions.has(item.itemgroupid);

      if (children && children.length > 0) {
        return (
          <Accordion
            key={item.itemgroupid}
            collapsed={!expandedAccordions.has(item.itemgroupid)}
            parent={
              <div className={`${styles.row} mb-2`}>
                <Radio
                  id={item.itemgroupid}
                  text={truncateGroupName(item.name)}
                  title={item.name}
                  name="itemGroup"
                  onChange={() => handleRadioChange(item.itemgroupid)}
                  checked={selectedRadio === item.itemgroupid}
                  value={item.itemgroupid}
                  className={styles.radioLabel}
                  textClassName={styles.radioText}
                />
                <div className={styles.arrowWrap}>
                  <Icon
                    size={20}
                    className={`cursor-pointer transition-transform ${
                      isExpanded ? "rotate-180" : "rotate-90"
                    }`}
                    onClick={() => toggleAccordion(item.itemgroupid)}
                    type="arrow"
                  />
                </div>
              </div>
            }
          >
            <div className="pl-5">{renderItems(children)}</div>
          </Accordion>
        );
      }

      return (
        <RadioContainer key={item.itemgroupid}>
          <div className={styles.row}>
            <Radio
              id={item.itemgroupid}
              text={truncateGroupName(item.name)}
              title={item.name}
              name="itemGroup"
              onChange={() => handleRadioChange(item.itemgroupid)}
              checked={selectedRadio === item.itemgroupid}
              value={item.itemgroupid}
              className={styles.radioLabel}
              textClassName={styles.radioText}
            />
          </div>
        </RadioContainer>
      );
    });

  return (
    <BaseLayout
      width={400}
      height={500}
      header={
        <div className="flex justify-between items-center">
          <BoldText>{title}</BoldText>
          <Icon type="close" onClick={closeModal} className="cursor-pointer" />
        </div>
      }
      footer={
        <div className="flex flex-row mt-4 justify-center">
          <Button onClick={closeModal} variant={EButtonVariant.TERTIARY}>
            Отменить
          </Button>
          <HorizontalSpace size={5} />
          <Button variant={EButtonVariant.SECONDARY} onClick={handleSave}>
            Перенести
          </Button>
        </div>
      }
    >
      <div style={{ height: 300, padding: 32 }}>
        {renderItems(productGroupItems)}
      </div>
    </BaseLayout>
  );
};
